import { useEffect, useState } from 'react'
import Modal from 'react-modal'
import { Link } from 'react-router-dom'
import { AdminEventJourneyUserDto, ApiError } from '@ivy-monorepo/shared'
import { BaseModalProps } from '../../types/modal'
import { adminService } from '../../services/admin.service'
import { formatEventSource, formatJourneyDate } from '../../utils/eventJourney'
import EventJourneyTimeline from '../EventJourneyTimeline'

type EventJourneyUserModalProps = BaseModalProps & {
  eventPublicId: string | null
  eventName?: string
  userPublicId: string | null
}

/** How one user reached an event: RSVP / ticket source plus every recorded step. */
const EventJourneyUserModal = ({ isOpen, onClose, eventPublicId, eventName, userPublicId }: EventJourneyUserModalProps) => {
  const [journey, setJourney] = useState<AdminEventJourneyUserDto | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!isOpen || !eventPublicId || !userPublicId) return
    setJourney(null)
    setLoadError(null)
    setIsLoading(true)
    adminService
      .getEventJourneyUsers(eventPublicId, 1, 1, 'all', userPublicId)
      .then(response => setJourney(response.users[0] ?? null))
      .catch((err: ApiError) => setLoadError(err.friendlyMessage || err.message || 'Failed to load the journey.'))
      .finally(() => setIsLoading(false))
  }, [isOpen, eventPublicId, userPublicId])

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      className="modal-popup event-journey-modal"
      overlayClassName="modal-overlay"
      contentLabel="Event journey"
    >
      <div className="modal-content-wrapper">
        <div className="modal-body-content">
          {loadError && <div className="page-error">{loadError}</div>}
          {isLoading && <div className="page-loading">Loading…</div>}
          {!isLoading && !loadError && !journey && <p className="event-journey-empty">No recorded steps for this user.</p>}
          {journey && (
            <>
              <h3>
                <Link to={`/admin/users/${journey.user.publicId}`}>
                  {journey.user.firstName} {journey.user.lastName}
                </Link>
                {eventName ? ` → ${eventName}` : ''}
              </h3>

              <section className="event-journey-section">
                <dl>
                  <div>
                    <dt>RSVP</dt>
                    <dd>{journey.rsvpedAt ? `${formatJourneyDate(journey.rsvpedAt)} · from ${formatEventSource(journey.rsvpSource)}` : '—'}</dd>
                  </div>
                  <div>
                    <dt>Ticket</dt>
                    <dd>{journey.ticketStatus ? `${journey.ticketStatus} · from ${formatEventSource(journey.ticketSource)}` : '—'}</dd>
                  </div>
                </dl>
              </section>

              <section className="event-journey-section">
                <h4>Steps</h4>
                <EventJourneyTimeline steps={journey.steps} />
              </section>
            </>
          )}
        </div>
        <div className="modal-footer-content">
          {eventPublicId && (
            <Link className="styled-button event-journey-modal__link" to={`/admin/venue-events/${eventPublicId}/journey`} onClick={onClose}>
              All journeys for this event
            </Link>
          )}
          <button type="button" className="styled-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default EventJourneyUserModal
